import * as React from "react";
import CheckboxMui from "@mui/material/Checkbox";
import SvgIcon from "@mui/material/SvgIcon";
import { useTheme } from "@mui/material/styles";

const BOX_PX = {
  small: 18,
  medium: 22,
};

const DEFAULT_STROKE_WIDTH = 2.5;

const UncheckedIcon = React.forwardRef(
  (
    { sx, sizePx = BOX_PX.small, outlineColor = "currentColor", ...muiProps },
    ref
  ) => (
    <SvgIcon
      ref={ref}
      viewBox="0 0 24 24"
      {...muiProps}
      sx={[
        {
          fontSize: sizePx,
          width: `${sizePx}px`,
          height: `${sizePx}px`,
          overflow: "hidden",
          flexShrink: 0,
          display: "block",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      aria-hidden
    >
      <circle
        cx="12"
        cy="12"
        r="11.1"
        fill="none"
        stroke={outlineColor}
        strokeWidth="1.5"
      />
    </SvgIcon>
  )
);
UncheckedIcon.displayName = "CheckboxUncheckedIcon";

/**
 * Checked state: filled circle with thick white check.
 */
const CheckedIcon = React.forwardRef(
  (
    { sx, sizePx = BOX_PX.small, fillColor, checkStrokeWidth, ...muiProps },
    ref
  ) => (
    <SvgIcon
      ref={ref}
      viewBox="0 0 24 24"
      {...muiProps}
      sx={[
        {
          fontSize: sizePx,
          width: `${sizePx}px`,
          height: `${sizePx}px`,
          overflow: "hidden",
          flexShrink: 0,
          display: "block",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      aria-hidden
    >
      <circle cx="12" cy="12" r="11.75" fill={fillColor} />
      <polyline
        points="6.5,12 10.2,15.7 17.5,8.2"
        fill="none"
        stroke="#ffffff"
        strokeWidth={checkStrokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SvgIcon>
  )
);
CheckedIcon.displayName = "CheckboxCheckedIcon";

/**
 * Indeterminate state: filled circle with thick white bar.
 */
const IndeterminateIcon = React.forwardRef(
  (
    { sx, sizePx = BOX_PX.small, fillColor, barStrokeWidth, ...muiProps },
    ref
  ) => (
    <SvgIcon
      ref={ref}
      viewBox="0 0 24 24"
      {...muiProps}
      sx={[
        {
          fontSize: sizePx,
          width: `${sizePx}px`,
          height: `${sizePx}px`,
          overflow: "hidden",
          flexShrink: 0,
          display: "block",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      aria-hidden
    >
      <circle cx="12" cy="12" r="11.75" fill={fillColor} />
      <line
        x1="7"
        y1="12"
        x2="17"
        y2="12"
        stroke="#ffffff"
        strokeWidth={barStrokeWidth}
        strokeLinecap="round"
      />
    </SvgIcon>
  )
);
IndeterminateIcon.displayName = "CheckboxIndeterminateIcon";

const Checkbox = ({
  checked = false,
  indeterminate = false,
  disabled = false,
  size = "small",
  onChange = () => {},
  onClick,
  color,
  borderColor,
  checkStrokeWidth = DEFAULT_STROKE_WIDTH,
  sx = {},
  ...rest
}) => {
  const theme = useTheme();
  const sizePx = BOX_PX[size] ?? BOX_PX.small;

  // Disabled: muted ring when unchecked; neutral grey fill when checked/indeterminate
  const outlineResolved = disabled
    ? theme.palette.text.disabled
    : (borderColor ?? theme.palette.text.secondary ?? theme.palette.grey[600]);

  const fillResolved = disabled
    ? theme.palette.grey[theme.palette.mode === "dark" ? 600 : 400]
    : (color ?? theme.palette.primary.main);

  return (
    <CheckboxMui
      checked={checked}
      indeterminate={indeterminate}
      disabled={disabled}
      color="primary"
      size={size}
      onChange={onChange}
      onClick={onClick}
      disableRipple
      icon={<UncheckedIcon sizePx={sizePx} outlineColor={outlineResolved} />}
      checkedIcon={
        <CheckedIcon
          sizePx={sizePx}
          fillColor={fillResolved}
          checkStrokeWidth={checkStrokeWidth}
        />
      }
      indeterminateIcon={
        <IndeterminateIcon
          sizePx={sizePx}
          fillColor={fillResolved}
          barStrokeWidth={checkStrokeWidth}
        />
      }
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        p: `${4}px`,
        boxSizing: "border-box",
        "& .MuiSvgIcon-root": {
          opacity: 1,
          flexShrink: 0,
        },
        ...sx,
      }}
      {...rest}
    />
  );
};

export default Checkbox;
